// Resolve a trip stay's destId -> [lng, lat] for the map's itinerary layer.
//
// The dataset-independent stay overlay (stay-overlay.ts) needs a point for each
// stay regardless of whether the trip sits on a curated transit network. Most
// destIds resolve from the slim /dest-coords.json map (GeoNames place ids +
// curated city centers, generated by scripts/build_dest_coords.mjs). The map is
// lazy-fetched once and memoized — only loaded when a map surface actually
// mounts.

import { customSlugToQuery } from './trip-hero';

export type LngLat = [number, number];

let coordsMap: Promise<Record<string, LngLat>> | null = null;
// Per-destId geocode cache for the custom-slug fallback (dedupes + survives the
// page session). Maps a destId to its resolved [lng,lat] or null (miss).
const geoCache = new Map<string, Promise<LngLat | null>>();

function loadDestCoords(): Promise<Record<string, LngLat>> {
  if (!coordsMap) {
    coordsMap = fetch('/dest-coords.json')
      .then((r) => {
        const ct = r.headers.get('content-type') || '';
        // The SPA fallback 200s any unknown path with index.html — guard on the
        // content-type so a missing asset degrades to "no coords", not a throw.
        return r.ok && ct.includes('json') ? (r.json() as Promise<Record<string, LngLat>>) : {};
      })
      .catch(() => ({}));
  }
  return coordsMap;
}

/**
 * Resolve every stay's destId to coordinates. Returns a destId -> [lng, lat]
 * map covering only the stays that resolved (missing ones are simply absent —
 * the caller omits a dot rather than guessing). `custom:`-prefixed picker slugs
 * are tried with and without the prefix, mirroring the hero-query resolver.
 *
 * `catalog` (optional) is the trip's already-loaded catalog manifest: a stay
 * whose bare slug matches a catalog entry id (e.g. custom:shoraiso → the booked
 * ryokan hotels/shoraiso) takes that entry's coords. Checked before the geocode
 * fallback — a property name like "Shoraiso" has no Wikipedia/Nominatim page,
 * so without this the stay silently loses its stop coin.
 */
export async function resolveStayCoords(
  stays: Array<{ destId?: string | null; label?: string | null }> | null | undefined,
  catalog?: { entries: Array<{ id: string; coords?: { lat: number; lng: number } | null }> } | null,
): Promise<Map<string, LngLat>> {
  const out = new Map<string, LngLat>();
  const ids = Array.from(new Set((stays ?? []).map((s) => s.destId).filter((x): x is string => !!x)));
  if (!ids.length) return out;
  // The stay label preserves the picker's original text ("Snow Monkey
  // Park(Jigokudani Yaen Koen)") that the slug lossy-encodes — the geocode
  // fallback works much better from it than from a de-slugged id.
  const labelById = new Map<string, string>();
  for (const s of stays ?? []) {
    if (s.destId && s.label && !labelById.has(s.destId)) labelById.set(s.destId, s.label);
  }
  const map = await loadDestCoords();
  let unresolved: string[] = [];
  for (const destId of ids) {
    const hit =
      map[destId] ??
      (destId.startsWith('custom:') ? map[destId.slice('custom:'.length)] : undefined);
    if (hit) out.set(destId, hit);
    else unresolved.push(destId);
  }
  // Catalog-entry match: the stay names a specific catalog place (a booked
  // ryokan, an attraction) rather than a geocodable city. Exact-property coords
  // beat a city-center geocode, and it costs no network — the caller already
  // holds the manifest.
  if (unresolved.length && catalog?.entries?.length) {
    const byId = new Map<string, LngLat>();
    for (const e of catalog.entries) {
      const c = e.coords;
      if (c && typeof c.lat === 'number' && typeof c.lng === 'number' && !byId.has(e.id)) {
        byId.set(e.id, [c.lng, c.lat]);
      }
    }
    unresolved = unresolved.filter((destId) => {
      const bare = destId.startsWith('custom:') ? destId.slice('custom:'.length) : destId;
      const hit = byId.get(destId) ?? byId.get(bare);
      if (hit) out.set(destId, hit);
      return !hit;
    });
  }
  // Fallback for picker-created `custom:` slugs (e.g. custom:dunsmuir-ca-usa),
  // which aren't in the prebuilt map: geocode them via the destination-stub
  // materializer. Idempotent + cached, and it also enriches the place into a
  // real catalog destination. A miss just leaves the stay un-dotted.
  await Promise.all(
    unresolved.map(async (destId) => {
      const c = await geocodeCustomSlug(destId, labelById.get(destId));
      if (c) out.set(destId, c);
    }),
  );
  return out;
}

/**
 * Materialize a custom destination's stub (coords + Wikipedia/Nominatim facts)
 * and return its coords. Idempotent + cached. Call this EAGERLY when a custom
 * destination is added from a picker — pass the picker's rich name (e.g.
 * "Dunsmuir, California") for a precise geocode, and the Google placeId when
 * the pick came from Places autocomplete (exact coords, no geocode guessing) —
 * so the place is a real catalog destination by the time the map loads,
 * instead of geocoding lazily then.
 */
export function ensureDestinationStub(
  destId: string,
  name?: string,
  country?: string,
  placeId?: string,
): Promise<LngLat | null> {
  return geocodeCustomSlug(destId, name, country, placeId);
}

function geocodeCustomSlug(
  destId: string,
  nameOverride?: string,
  countryOverride?: string,
  placeId?: string,
): Promise<LngLat | null> {
  const cached = geoCache.get(destId);
  if (cached) return cached;
  const p = (async (): Promise<LngLat | null> => {
    if (!destId.startsWith('custom:')) return null; // only picker custom slugs
    const slug = destId.slice('custom:'.length);
    const name = nameOverride || customSlugToQuery(slug); // "dunsmuir-ca-usa" → "Dunsmuir, California"
    const country = countryOverride || (/-usa$/.test(slug) ? 'United States' : undefined);
    try {
      const res = await fetch(`/api/destinations/${encodeURIComponent(slug)}/stub`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, country, ...(placeId ? { placeId } : {}) }),
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok || !ct.includes('json')) return null;
      const body = (await res.json()) as { stub?: { coords?: { lat?: number; lng?: number } } };
      const c = body?.stub?.coords;
      return c && typeof c.lat === 'number' && typeof c.lng === 'number' ? [c.lng, c.lat] : null;
    } catch {
      return null;
    }
  })();
  geoCache.set(destId, p);
  return p;
}
